import { useEffect, useState } from "react";
import { api } from "../api/client";

export interface LiveRank {
  rank: number;
  score: number;
}

/**
 * Subscribe to a player's **live rank** on each of `boardIds` over the existing leaderboard
 * WebSocket (the `rank_of` view), and return a `{ [boardId]: { rank, score } }` map that updates
 * reactively as scores change. One socket per board; the owner recomputes the view on every board
 * change and pushes a self-contained frame (`entries[0]` is this player's current standing), so we
 * just replace our copy each frame.
 *
 * Auth uses a short-lived **operator** subscribe token (`api.viewToken`) — unlike a public-view
 * token it reaches private boards. The token is re-minted on (re)connect, which also handles expiry.
 * No-ops in mock mode (no real backend) — callers fall back to the ledger's latest score.
 */
export function useLiveRanks(
  projectId: string,
  playerId: string | null,
  boardIds: string[],
): Record<string, LiveRank> {
  const [ranks, setRanks] = useState<Record<string, LiveRank>>({});
  const key = boardIds.join(",");

  useEffect(() => {
    setRanks({});
    if (api.isMock || !playerId || boardIds.length === 0) return;

    let closed = false;
    const sockets: WebSocket[] = [];
    const wsBase = ((import.meta.env.VITE_API_BASE ?? "") || location.origin).replace(/^http/, "ws");

    async function connect(board: string, attempt: number) {
      if (closed) return;
      let token: string;
      try {
        token = (await api.viewToken(projectId)).token;
      } catch {
        return; // can't mint — leave the card on its ledger fallback
      }
      if (closed) return;
      const url = `${wsBase}/v1/leaderboards/${encodeURIComponent(board)}/subscribe?token=${encodeURIComponent(token)}`;
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }
      sockets.push(ws);
      ws.onopen = () =>
        ws.send(JSON.stringify({ op: "sub", view: "rank_of", player_id: playerId }));
      ws.onmessage = (ev) => {
        let f: { type?: string; view?: string; entries?: { rank: number; score: number }[] };
        try {
          f = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        if ((f.type === "snapshot" || f.type === "delta") && f.view === "rank_of") {
          const e = f.entries?.[0];
          setRanks((prev) => {
            const next = { ...prev };
            if (e) next[board] = { rank: e.rank, score: e.score };
            else delete next[board]; // player no longer ranked on this board
            return next;
          });
        }
      };
      ws.onclose = () => {
        if (closed || attempt > 5) return;
        // Reconnect with a fresh token (handles operator-token expiry) after a short backoff.
        setTimeout(() => connect(board, attempt + 1), 1500 * (attempt + 1));
      };
    }

    boardIds.forEach((b) => connect(b, 0));
    return () => {
      closed = true;
      sockets.forEach((s) => {
        try {
          s.close();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, playerId, key]);

  return ranks;
}
